/**
 * 電子發票證明聯(可列印 / 另存 PDF)— REQ-20260602001 Phase 2C-3。
 * 1D 條碼與 QR 內容採用 ezPay 開立時回傳的值(REQ-20260915003);本頁只負責渲染 + 列印。
 */
import { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import type { InvoiceCertificate } from '@app/shared';
import { ExportJpegButton } from '../components/ExportJpegButton';
import { buildDocumentFilename } from '../lib/filename';
import { useInvoiceCertificate } from '../features/invoices/api';

export function CertificatePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const invId = id ? Number(id) : undefined;
  const { data, isLoading, isError, error } = useInvoiceCertificate(invId);

  /*
   * 存檔預設檔名(REQ-20260903006)。本頁原本沒設過 `document.title`,證明聯存出來
   * 都叫「訪談系統」。做法照抄 REQ-20260811002:設 `document.title`(不引 PDF 套件),
   * cleanup 還原成進來之前的值,而且 hook 要掛在底下的早退**之前**。
   */
  const documentFilename = buildDocumentFilename(['發票證明聯', data?.invoiceNo]);
  useEffect(() => {
    const previousTitle = document.title;
    document.title = documentFilename;
    return () => {
      document.title = previousTitle;
    };
  }, [documentFilename]);

  /** 「另存 JPEG」要截的那一塊:證明聯本體。這頁不是 `.print-area`,它自帶一份 PRINT_CSS */
  const sheetRef = useRef<HTMLDivElement>(null);

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError || !data) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
      </div>
    );
  }

  return (
    <div className="grid gap-4" ref={sheetRef}>
      <div className="flex items-center gap-3 no-print">
        <Link to="/invoices" className="text-blue-600 hover:underline text-sm">
          ← 回發票列表
        </Link>
        {/* 同一份證明聯的第二種存檔格式(REQ-20260903006);PDF 那條路一行未動 */}
        <ExportJpegButton targetRef={sheetRef} filename={documentFilename} selector=".cert" />
        <button
          type="button"
          onClick={() => window.print()}
          className="border border-blue-600 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-50 text-sm"
        >
          列印 / 存 PDF
        </button>
      </div>

      <Certificate cert={data} />

      <style>{PRINT_CSS}</style>
    </div>
  );
}

function Certificate({ cert }: { cert: InvoiceCertificate }): JSX.Element {
  return (
    <div className="cert mx-auto bg-white text-black border border-slate-300 p-4">
      <div className="text-center font-bold text-lg">電子發票證明聯</div>
      <div className="text-center text-sm">{cert.periodLabel}</div>
      <div className="text-center font-mono text-xl tracking-wider">{cert.invoiceNo}</div>

      <div className="mt-1 text-sm flex justify-between">
        <span>{cert.invoiceDateLabel} {cert.invoiceTime}</span>
        <span>隨機碼 {cert.randomNumber}</span>
      </div>
      <div className="text-sm flex justify-between">
        <span>賣方 {cert.sellerTaxId}</span>
        {cert.buyerTaxId ? <span>買方 {cert.buyerTaxId}</span> : null}
      </div>

      <div className="my-1 text-right text-base font-semibold">
        總計 {Number(cert.totalAmount).toLocaleString()}
      </div>

      {/* 1D Code39 條碼 */}
      <div className="flex flex-col items-center my-2">
        <Code39 value={cert.barcode1D} />
        <div className="font-mono text-[10px] tracking-wider">{cert.barcode1D}</div>
      </div>

      {/* 兩段式 QR */}
      <div className="flex justify-center gap-6 my-2">
        <QRCodeSVG value={cert.qrLeft} size={120} level="L" />
        <QRCodeSVG value={cert.qrRight} size={120} level="L" />
      </div>

      {/* 品項 */}
      <table className="w-full text-xs border-t border-slate-300 mt-2">
        <thead>
          <tr className="text-slate-600">
            <th className="text-left py-0.5">品名</th>
            <th className="text-right py-0.5">數量</th>
            <th className="text-right py-0.5">單價</th>
            <th className="text-right py-0.5">金額</th>
          </tr>
        </thead>
        <tbody>
          {cert.lines.map((l, i) => (
            <tr key={i}>
              <td className="py-0.5">{l.description}</td>
              <td className="text-right py-0.5">{l.quantity}</td>
              <td className="text-right py-0.5">{l.unitPrice}</td>
              <td className="text-right py-0.5">{l.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="text-xs text-right mt-1">
        銷售額 {cert.salesAmount}　稅額 {cert.taxAmount}　總計 {cert.totalAmount}
      </div>
    </div>
  );
}

/** Code39 條碼(SVG,免依賴)。narrow=2px、wide=6px、bar 黑 / space 白 */
function Code39({ value }: { value: string }): JSX.Element {
  const NARROW = 2;
  const WIDE = 6;
  const H = 48;
  const content = `*${value.toUpperCase()}*`;
  const rects: JSX.Element[] = [];
  let x = 0;
  let key = 0;

  for (const ch of content) {
    const pat = CODE39[ch];
    if (!pat) continue;
    for (let e = 0; e < pat.length; e++) {
      const w = pat[e] === 'w' ? WIDE : NARROW;
      const isBar = e % 2 === 0; // 元素 0,2,4,6,8 為 bar
      if (isBar) {
        rects.push(<rect key={key++} x={x} y={0} width={w} height={H} fill="black" />);
      }
      x += w;
    }
    x += NARROW; // 字元間隔
  }

  return (
    <svg width={x} height={H} viewBox={`0 0 ${x} ${H}`} role="img" aria-label={value}>
      {rects}
    </svg>
  );
}

// Code39 編碼表:每字元 9 元素(bar,space 交替,共 5 bar 4 space),n=narrow w=wide
const CODE39: Record<string, string> = {
  '0': 'nnnwwnwnn', '1': 'wnnwnnnnw', '2': 'nnwwnnnnw', '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw', '5': 'wnnwwnnnn', '6': 'nnwwwnnnn', '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn', '9': 'nnwwnnwnn',
  A: 'wnnnnwnnw', B: 'nnwnnwnnw', C: 'wnwnnwnnn', D: 'nnnnwwnnw', E: 'wnnnwwnnn',
  F: 'nnwnwwnnn', G: 'nnnnnwwnw', H: 'wnnnnwwnn', I: 'nnwnnwwnn', J: 'nnnnwwwnn',
  K: 'wnnnnnnww', L: 'nnwnnnnww', M: 'wnwnnnnwn', N: 'nnnnwnnww', O: 'wnnnwnnwn',
  P: 'nnwnwnnwn', Q: 'nnnnnnwww', R: 'wnnnnnwwn', S: 'nnwnnnwwn', T: 'nnnnwnwwn',
  U: 'wwnnnnnnw', V: 'nwwnnnnnw', W: 'wwwnnnnnn', X: 'nwnnwnnnw', Y: 'wwnnwnnnn',
  Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw', '.': 'wwnnnnwnn', ' ': 'nwwnnnwnn',
  '*': 'nwnnwnwnn',
};

const PRINT_CSS = `
.cert { width: 80mm; }
@media print {
  .no-print { display: none !important; }
  body { background: white; }
  .cert { border: none; margin: 0; }
}
`;
